import { isEqual } from 'lodash'
import { makeAutoObservable } from 'mobx'
import { CameraStore } from 'store/profile/CameraStore'
import { SelfieStore } from 'store/profile/SelfieStore'
import { Identity } from 'type/Identity'
import { LengthUnit, loadUnit, saveUnit } from 'type/LengthUnit'
import { getUrlPath } from 'util/url'

export type BannerState = 'open' | 'hide' | 'close'

type Saved = {
  pics: [string, string, string]
}

export class ProfileCommonFormStore {
  private _unit: LengthUnit = loadUnit()
  readonly selfies = [new SelfieStore(), new SelfieStore()] as const
  readonly userPhoto = new SelfieStore({ fullbody: true })
  camera = new CameraStore()
  private _banner: BannerState = 'close'
  private _num: 0 | 1 = 0
  readonly saved: Saved = { pics: ['', '', ''] }
  private discarded?: string
  private restore?: [string, string, string]
  private soloUserPhoto = false

  constructor(identity: Identity | undefined) {
    makeAutoObservable(this)
    this.init(identity)
    this.save()
  }

  get both(): boolean {
    return false
  }

  get unit(): LengthUnit {
    return this._unit
  }

  set unit(unit: LengthUnit) {
    saveUnit(unit)
    this._unit = unit
  }

  get banner(): BannerState {
    return this._banner
  }

  set banner(value: BannerState) {
    this._banner = value
  }

  get num(): 0 | 1 {
    return this._num
  }

  set num(value: 0 | 1) {
    this._num = value
  }

  reset() {
    this.apply()
    this.camera = new CameraStore()
  }

  get canSave(): boolean {
    const selfieUrl = !!this.selfies[0].url || !!this.selfies[1].url
    const userPhotoUrl = !!this.userPhoto.url
    const url = selfieUrl || userPhotoUrl
    const uploading = this.selfies[0].uploading || this.selfies[1].uploading || this.userPhoto.uploading
    return url && !uploading
  }

  async beforeSave() {
    await this.userPhoto.applyRotation()
  }

  afterRequest() {
    this.save()
  }

  get pics(): string[] {
    return [this.selfies[0].url || '', this.selfies[1].url || '', this.userPhoto.url || '']
  }

  get pic(): string | undefined {
    return this.pics.find(it => it) || undefined
  }

  get pic_change(): 0 | 1 {
    const same = isEqual(this.saved.pics, this.pics)
    return same ? 0 : 1
  }

  get picture() {
    return this.selfies[this.num].url
  }

  retake(photo: boolean) {
    const selfie = photo ? this.userPhoto : this.selfies[this.num]
    selfie.clear(true)
    void this.camera.start()
  }

  /**
   * «Загрузить другое фото»: на сервере в identity лежит прежнее фото, и обычный clear() ничего
   * не даёт — опрос identity, которым экран с QR ждёт новое фото, через секунду вернёт старое.
   * Поэтому отброшенный url запоминаем и не принимаем обратно, пока не приедет другой.
   */
  discardUserPhoto() {
    this.restore = [...this.pics] as [string, string, string]
    this.soloUserPhoto = true
    this.discarded = getUrlPath(this.userPhoto.url)
    // Чистим всё: после загрузки по QR в профиле должна остаться только новая картинка.
    this.userPhoto.clear()
    this.selfies[0].clear()
    this.selfies[1].clear()
    // Иначе reset() при смене экрана вернёт отброшенные фото из saved.
    this.saved.pics = ['', '', '']
  }

  // «Отмена» на экране с QR: откатываем сброс целиком и снова принимаем прежнее фото из identity.
  restoreUserPhoto(): boolean {
    const pics = this.restore
    if (!pics?.[2]) return false
    this.soloUserPhoto = false
    this.discarded = undefined
    this.restore = undefined
    this.selfies[0].setUrl(pics[0])
    this.selfies[1].setUrl(pics[1])
    this.userPhoto.setUrl(pics[2])
    this.save()
    return true
  }

  applyUserPhoto(url: string | undefined) {
    if (this.discarded && (!url || getUrlPath(url) === this.discarded)) return
    this.discarded = undefined
    this.restore = undefined
    this.userPhoto.setUrl(url)
  }

  // После сброса фото и до сохранения профиля старые селфи из identity не принимаем:
  // в профиле должна остаться только одна новая картинка.
  applySelfies(face1: string | undefined, face2: string | undefined) {
    if (this.soloUserPhoto) return
    this.selfies[0].setUrl(face1)
    this.selfies[1].setUrl(face2)
  }

  async take(photo?: boolean) {
    const selfie = photo ? this.userPhoto : this.selfies[this.num]
    await this.camera.take(selfie)
  }

  private init(it: Identity | undefined) {
    this.selfies[0].setUrl(it?.face1_url)
    this.selfies[1].setUrl(it?.face2_url)
    this.userPhoto.setUrl(it?.user_url || it?.body1_url)
  }

  private apply() {
    this.selfies[0].setUrl(this.saved.pics[0])
    this.selfies[1].setUrl(this.saved.pics[1])
    this.userPhoto.setUrl(this.saved.pics[2])
  }

  private save() {
    // Профиль сохранён — дальше принимаем всё, что приходит из identity.
    this.soloUserPhoto = false
    this.saved.pics[0] = this.selfies[0].url || ''
    this.saved.pics[1] = this.selfies[1].url || ''
    this.saved.pics[2] = this.userPhoto.url || ''
  }
}
